import React, { useState, useEffect, useRef } from 'react';
import { batchApi } from '../../../api/batchApi';
import { BatchJobConfig, BatchJobConfigFormData } from '../../../types/batch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Alert, AlertDescription } from '../../ui/alert';
import { Plus, Edit2, Trash2, Save, X, Power, PowerOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL as BASE } from '../../../api/apiConfig';

export const BatchConfigManager: React.FC = () => {
  const [configs, setConfigs] = useState<BatchJobConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<BatchJobConfig | null>(null);
  const [cronPreset, setCronPreset] = useState<string>('custom');
  
  // 수정 폼으로 스크롤하기 위한 ref
  const formRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<BatchJobConfigFormData>({
    jobType: '',
    displayName: '',
    description: '',
    cronExpression: '',
    scheduleDescription: '',
    isActive: true,
    isAllowConcurrent: false,
    timeoutMinutes: 30,
    notificationEmails: []
  });

  // Cron 표현식 프리셋
  const cronPresets = [
    { value: '0 */5 * * * ?', label: '5분마다', description: '5분마다' },
    { value: '0 */10 * * * ?', label: '10분마다', description: '10분마다' },
    { value: '0 */30 * * * ?', label: '30분마다', description: '30분마다' },
    { value: '0 0 * * * ?', label: '매 시간 정각', description: '매 시간 정각' },
    { value: '0 0 0 * * ?', label: '매일 자정', description: '매일 자정' },
    { value: '0 0 3 * * ?', label: '매일 오전 3시', description: '매일 오전 3시' },
    { value: '0 30 3 * * ?', label: '매일 오전 3시 30분', description: '매일 오전 3시 30분' },
    { value: '0 0 4 * * ?', label: '매일 오전 4시', description: '매일 오전 4시' },
    { value: '0 0 0 1 * ?', label: '매월 1일 자정', description: '매월 1일 자정' },
    { value: 'custom', label: '직접 입력', description: '' },
  ];

  // 이메일 검증 함수
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmails = (emails: string[]): boolean => {
    if (emails.length === 0) return true; // 이메일 없어도 OK
    return emails.every(email => validateEmail(email));
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await batchApi.getAllBatchConfigs();
      
      if (response.data.success) {
        const dataArray = response.data.data || [];
        setConfigs(dataArray);
      } else {
        const errorMsg = response.data.message || '알 수 없는 에러';
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('[BatchConfigManager] API 호출 실패:', err.response?.data || err.message);
      
      const errorMsg = err.response?.data?.message || err.message || '배치 설정 조회 실패';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.jobType.trim() || !formData.displayName.trim()) {
      setError('배치 타입과 이름은 필수입니다');
      return;
    }

    if (!formData.cronExpression.trim()) {
      setError('Cron 표현식은 필수입니다');
      return;
    }

    if (!validateEmails(formData.notificationEmails)) {
      setError('올바른 이메일 형식이 아닙니다');
      return;
    }

    try {
      setIsLoading(true);
      const response = await batchApi.createBatchConfig(formData);
      if (response.data.success) {
        setSuccess('배치 설정이 추가되었습니다');
        setShowAddForm(false);
        resetForm();
        loadConfigs();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '배치 설정 추가 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingConfig) return;

    if (!validateEmails(formData.notificationEmails)) {
      setError('올바른 이메일 형식이 아닙니다');
      return;
    }

    try {
      setIsLoading(true);
      const response = await batchApi.updateBatchConfig(editingConfig.id, formData);
      if (response.data.success) {
        setSuccess('배치 설정이 수정되었습니다');
        setEditingConfig(null);
        resetForm();
        loadConfigs();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '배치 설정 수정 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number, displayName: string) => {
    if (!window.confirm(
      `"${displayName}" 배치를 삭제하시겠습니까?\n\n` +
      `⚠️ 주의: 이 작업은 되돌릴 수 없으며, 스케줄러가 더 이상 실행되지 않습니다.\n` +
      `운영 중인 배치를 삭제하면 시스템에 영향을 줄 수 있습니다.`
    )) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await batchApi.deleteBatchConfig(id);
      if (response.data.success) {
        setSuccess('배치 설정이 삭제되었습니다');
        loadConfigs();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '배치 설정 삭제 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      setIsLoading(true);
      const response = await batchApi.toggleBatchActive(id, isActive);
      if (response.data.success) {
        setSuccess(`배치가 ${isActive ? '활성화' : '비활성화'}되었습니다`);
        loadConfigs();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '배치 상태 변경 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (config: BatchJobConfig) => {
    setEditingConfig(config);
    setFormData({
      jobType: config.jobType,
      displayName: config.displayName,
      description: config.description || '',
      cronExpression: config.cronExpression,
      scheduleDescription: config.scheduleDescription,
      isActive: config.isActive,
      isAllowConcurrent: config.isAllowConcurrent,
      timeoutMinutes: config.timeoutMinutes,
      notificationEmails: config.notificationEmails
    });
    
    // Cron 프리셋 찾기
    const preset = cronPresets.find(p => p.value === config.cronExpression);
    setCronPreset(preset ? preset.value : 'custom');
    
    setShowAddForm(false);
    
    // 수정 폼으로 부드럽게 스크롤 (center로 위치하여 전체 폼이 잘 보이도록)
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const resetForm = () => {
    setFormData({
      jobType: '',
      displayName: '',
      description: '',
      cronExpression: '',
      scheduleDescription: '',
      isActive: true,
      isAllowConcurrent: false,
      timeoutMinutes: 30,
      notificationEmails: []
    });
    setEditingConfig(null);
    setCronPreset('custom');
  };

  const cancelEdit = () => {
    resetForm();
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl">⚙️ 배치 현황</CardTitle>
              <CardDescription>배치 작업의 설정을 관리합니다 (배치명, 주기, 시각)</CardDescription>
            </div>
            <Button 
              onClick={() => {
                setShowAddForm(!showAddForm);
                setEditingConfig(null);
              }}
              disabled={isLoading}
            >
              {showAddForm ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  취소
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  배치 추가
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 border-green-200 mb-4">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          {/* 추가/수정 폼 */}
          {(showAddForm || editingConfig) && (
            <div ref={formRef} className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-bold mb-4">
                {editingConfig ? '배치 설정 수정' : '새 배치 추가'}
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      배치 타입 (코드) * {editingConfig && <span className="text-xs text-red-500">(수정 불가)</span>}
                    </label>
                    <Input
                      value={formData.jobType}
                      onChange={(e) => setFormData({ ...formData, jobType: e.target.value.toUpperCase() })}
                      placeholder="예: ALT_TEXT_GENERATION"
                      disabled={!!editingConfig}
                      className={editingConfig ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                    {!editingConfig && (
                      <p className="text-xs text-gray-500 mt-1">
                        영문 대문자와 언더스코어만 사용 (예: NEW_BATCH_JOB)
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      배치명 *
                    </label>
                    <Input
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      placeholder="예: ALT 텍스트 자동 생성"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    설명
                  </label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="배치 작업에 대한 설명을 입력하세요"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Cron 표현식 프리셋
                    </label>
                    <select
                      value={cronPreset}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCronPreset(value);
                        if (value !== 'custom') {
                          const preset = cronPresets.find(p => p.value === value);
                          if (preset) {
                            setFormData({ 
                              ...formData, 
                              cronExpression: preset.value,
                              scheduleDescription: preset.description
                            });
                          }
                        }
                      }}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {cronPresets.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Cron 표현식 * {cronPreset === 'custom' && '(직접 입력)'}
                    </label>
                    <Input
                      value={formData.cronExpression}
                      onChange={(e) => {
                        setFormData({ ...formData, cronExpression: e.target.value });
                        setCronPreset('custom');
                      }}
                      placeholder="예: 0 */5 * * * ?"
                      disabled={cronPreset !== 'custom'}
                      className={cronPreset !== 'custom' ? 'bg-gray-100' : ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      초 분 시 일 월 요일 (Spring Cron)
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    스케줄 설명 *
                  </label>
                  <Input
                    value={formData.scheduleDescription}
                    onChange={(e) => setFormData({ ...formData, scheduleDescription: e.target.value })}
                    placeholder="예: 5분마다"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      타임아웃 (분)
                    </label>
                    <Input
                      type="number"
                      value={formData.timeoutMinutes}
                      onChange={(e) => setFormData({ ...formData, timeoutMinutes: Number(e.target.value) })}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-7">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium">
                      활성화
                    </label>
                  </div>

                  <div className="flex items-center gap-2 pt-7">
                    <input
                      type="checkbox"
                      id="isAllowConcurrent"
                      checked={formData.isAllowConcurrent}
                      onChange={(e) => setFormData({ ...formData, isAllowConcurrent: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isAllowConcurrent" className="text-sm font-medium">
                      중복 실행 허용
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    알림 이메일 (콤마로 구분)
                  </label>
                  <Input
                    value={formData.notificationEmails.join(', ')}
                    onChange={(e) => {
                      const emails = e.target.value
                        .split(',')
                        .map(email => email.trim())
                        .filter(Boolean);
                      setFormData({ 
                        ...formData, 
                        notificationEmails: emails
                      });
                    }}
                    onBlur={(e) => {
                      const emails = e.target.value
                        .split(',')
                        .map(email => email.trim())
                        .filter(Boolean);
                      if (emails.length > 0 && !validateEmails(emails)) {
                        setError('올바른 이메일 형식이 아닙니다');
                      }
                    }}
                    placeholder="admin@example.com, manager@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    배치 실패 또는 타임아웃 발생 시 알림을 받을 이메일 주소
                  </p>
                  {formData.notificationEmails.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.notificationEmails.map((email, index) => (
                        <span 
                          key={index} 
                          className={`px-2 py-1 rounded text-xs ${
                            validateEmail(email) 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {email}
                          {validateEmail(email) ? ' ✓' : ' ✗'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={editingConfig ? handleUpdate : handleAdd} disabled={isLoading}>
                    <Save className="w-4 h-4 mr-2" />
                    {editingConfig ? '수정' : '추가'}
                  </Button>
                  <Button variant="outline" onClick={cancelEdit}>
                    <X className="w-4 h-4 mr-2" />
                    취소
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 배치 설정 목록 */}
          <div className="space-y-3">
            {isLoading && configs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-600 font-medium mb-2">❌ 데이터 로드 실패</div>
                <div className="text-sm text-gray-600 mb-4">{error}</div>
                <Button onClick={loadConfigs} variant="outline">
                  다시 시도
                </Button>
                <div className="mt-4 p-4 bg-gray-100 rounded text-xs text-left">
                  <div className="font-bold mb-2">디버그 정보:</div>
                  <div>• API 엔드포인트: {BASE}/api/admin/batch/config</div>
                  <div>• 브라우저 콘솔에서 상세 에러를 확인하세요 (F12)</div>
                  <div>• 백엔드 서버가 실행 중인지 확인하세요</div>
                  <div>• 로그인 토큰이 유효한지 확인하세요</div>
                </div>
              </div>
            ) : configs.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-2">등록된 배치 설정이 없습니다</div>
                <div className="text-sm text-gray-400 mb-4">
                  "배치 추가" 버튼을 클릭하여 새로운 배치를 등록하세요
                </div>
                <Button onClick={loadConfigs} variant="outline" size="sm">
                  새로고침
                </Button>
              </div>
            ) : (
              configs.map((config) => (
                <div
                  key={config.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-bold text-lg">{config.displayName}</h4>
                      <span className="text-xs px-2 py-1 bg-gray-200 rounded font-mono">
                        {config.jobType}
                      </span>
                      {config.isActive ? (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded flex items-center gap-1">
                          <Power className="w-3 h-3" />
                          활성
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded flex items-center gap-1">
                          <PowerOff className="w-3 h-3" />
                          비활성
                        </span>
                      )}
                      {config.isAllowConcurrent && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          중복 실행 허용
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      ⏰ {config.scheduleDescription} | 
                      ⏱️ 타임아웃: {config.timeoutMinutes}분 | 
                      📧 알림: {config.notificationEmails.length > 0 ? config.notificationEmails.length + '명' : '없음'}
                    </div>
                    {config.description && (
                      <div className="mt-1 text-sm text-gray-500">{config.description}</div>
                    )}
                    <div className="mt-1 text-xs text-gray-400 font-mono">
                      Cron: {config.cronExpression}
                    </div>
                  </div>

                  <div className="flex gap-2 sm:ml-4 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(config.id, !config.isActive)}
                      title={config.isActive ? '비활성화' : '활성화'}
                    >
                      {config.isActive ? (
                        <PowerOff className="w-4 h-4" />
                      ) : (
                        <Power className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(config)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(config.id, config.displayName)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
